import React, { useState, useEffect, useRef } from 'react';
import { Star, ShieldCheck, Quote } from 'lucide-react';
import { Badge } from './ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average_rating: 0, total_reviews: 0, five_star_count: 0 });
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetchFeaturedReviews();
  }, []);

  const fetchFeaturedReviews = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reviews/featured?limit=12`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data.featured_reviews || []);
        setStats(data.stats || { average_rating: 0, total_reviews: 0, five_star_count: 0 });
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating, size = 'w-3 h-3') => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${size} ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="w-full py-4 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center justify-center gap-2">
          <div className="animate-pulse h-4 bg-gray-200 rounded w-32"></div>
        </div>
      </div>
    );
  }

  // If no reviews yet, show compact prompt
  if (reviews.length === 0) {
    return (
      <div className="w-full py-3 bg-gradient-to-r from-blue-50 to-indigo-50" data-testid="reviews-section">
        <div className="flex items-center justify-center gap-3">
          {renderStars(0, 'w-4 h-4')}
          <span className="text-sm text-gray-500">Be the first to share your experience!</span>
        </div>
      </div>
    );
  }

  // Duplicate reviews for seamless infinite scroll
  const duplicatedReviews = [...reviews, ...reviews, ...reviews];

  return (
    <div className="w-full py-3 bg-gradient-to-r from-blue-50 via-white to-indigo-50 overflow-hidden" data-testid="reviews-section">
      {/* Compact Header */}
      <div className="flex items-center justify-center gap-4 mb-3 px-4">
        <div className="flex items-center gap-2">
          {renderStars(Math.round(stats.average_rating), 'w-4 h-4')}
          <span className="text-sm font-bold text-gray-900">{stats.average_rating.toFixed(1)}</span>
        </div>
        <span className="text-xs text-gray-500">|</span>
        <span className="text-xs text-gray-600">{stats.total_reviews} Reviews</span>
        {stats.five_star_count > 0 && (
          <>
            <span className="text-xs text-gray-500">|</span>
            <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs py-0 px-2">
              {stats.five_star_count} Five-Star
            </Badge>
          </>
        )}
      </div>

      {/* Scrolling Reviews Container */}
      <div 
        className="relative overflow-hidden"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Gradient Fade Left */}
        <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-blue-50 to-transparent z-10 pointer-events-none" />
        
        {/* Gradient Fade Right */}
        <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-indigo-50 to-transparent z-10 pointer-events-none" />

        {/* Scrolling Track */}
        <div 
          ref={scrollRef}
          className={`flex gap-3 ${isPaused ? 'animate-pause' : 'animate-marquee'}`}
          style={{
            width: 'max-content',
          }}
        >
          {duplicatedReviews.map((review, index) => (
            <div 
              key={`${review.id}-${index}`}
              className="flex-shrink-0 w-64 bg-white rounded-lg border border-gray-100 shadow-sm p-3 hover:shadow-md transition-shadow cursor-default"
              data-testid={`review-card-${index}`}
            >
              {/* Header Row */}
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5">
                  {/* Avatar */}
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">
                      {review.user_name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <span className="text-xs font-medium text-gray-800 truncate max-w-[80px]">
                    {review.user_name?.split(' ')[0] || 'User'}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {renderStars(review.rating)}
                  {review.verified_purchase && (
                    <ShieldCheck className="w-3 h-3 text-green-500 ml-1" />
                  )}
                </div>
              </div>

              {/* Title */}
              <p className="text-xs font-semibold text-gray-900 line-clamp-1 mb-1">
                {review.title}
              </p>

              {/* Comment */}
              <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">
                "{review.comment}"
              </p>

              {/* Product Badge (if product review) */}
              {review.review_type === 'product' && review.product_name && (
                <Badge variant="outline" className="mt-1.5 text-[10px] py-0 px-1.5 bg-blue-50 border-blue-200 text-blue-600">
                  {review.product_name}
                </Badge>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-33.33%);
          }
        }
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
        .animate-pause {
          animation-play-state: paused;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

export default ReviewsSection;
